import { FormatMessage, A11yPanelOptions } from "../types";
import { createFormatter } from "../utils";
import { buildLoader } from "../lib/buildLoader";

let formatMessage: FormatMessage;

const initializeFormatter = async (locale: string) => {
  formatMessage = await createFormatter(locale);
};

initializeFormatter("en-US");

const getWorkerUrl = (dev?: boolean): string => {
  return dev
    ? "https://a11y-panel-develop.vitalsource.com"
    : "https://a11y-panel.vitalsource.com";
};

const HTMX_URL = "https://unpkg.com/htmx.org@2.0.5/dist/htmx.min.js";

const appendStylesheet = (
  target: ShadowRoot | HTMLElement,
  href: string,
): void => {
  const getBaseFontSize = () => {
    const baseFontCalcTemporaryDiv = document.createElement("div");
    baseFontCalcTemporaryDiv.style.fontSize = "1rem";
    document.body.appendChild(baseFontCalcTemporaryDiv);
    const computedFontSize = window.getComputedStyle(
      baseFontCalcTemporaryDiv,
    ).fontSize;
    document.body.removeChild(baseFontCalcTemporaryDiv);
    return parseFloat(computedFontSize);
  };

  const getSizesStyleSheet = () => {
    const calculateRem = (size: number, base: number) => size / base + "rem";
    const maxSizeCount = 50; //max number of size variables

    const generateSizes = (base: number) => {
      let rootStyles = ":root, :host {";
      for (let i = 1; i <= maxSizeCount; i++) {
        rootStyles += `  --vst-size-${i}: ${calculateRem(i, base)};`;
      }
      rootStyles += "}";
      return rootStyles;
    };

    const cssStyles = generateSizes(getBaseFontSize());
    const stylesheet = document.createElement("style");
    stylesheet.innerText = cssStyles;
    return stylesheet;
  };

  const styleLink = document.createElement("link");
  styleLink.rel = "stylesheet";
  styleLink.href = href;

  const onStylesheetLoaded = () => {
    const sizeStylesheet = getSizesStyleSheet();
    target.insertBefore(sizeStylesheet, styleLink.nextSibling);
    target.dispatchEvent(new Event("stylesheetLoaded"));
  };

  styleLink.onload = onStylesheetLoaded;
  target.appendChild(styleLink);
};

const appendScript = (
  target: ShadowRoot | HTMLElement,
  src: string,
  onLoad: () => void,
): void => {
  const script = document.createElement("script");
  script.src = src;
  script.addEventListener("load", () => {
    // htmx config override selfRequestsOnly - default is true
    if (src.includes("htmx") && window.htmx) {
      window.htmx.config.selfRequestsOnly = false;
    }
    onLoad();
  });
  target.appendChild(script);
};

const setupShadowDom = (containerId: string, stylesUrl: string) => {
  let container = document.getElementById(containerId);
  if (!container) {
    container = document.createElement("div");
    container.id = containerId;
    document.body.appendChild(container);
  }

  if (!container.shadowRoot) {
    const shadowRoot = container.attachShadow({ mode: "open" });
    appendStylesheet(shadowRoot, stylesUrl);
    return { container, shadowRoot };
  }

  return { container, shadowRoot: container.shadowRoot };
};

const initializeHTMX = (
  shadowRoot: ShadowRoot,
  loaderHtml: string,
  workerUrl: string,
  vbid: string,
  locale?: string,
  theme?: string,
) => {
  if (shadowRoot.querySelector(".a11y-panel")) {
    return;
  }
  const shadowContent = document.createElement("div");
  shadowContent.classList.add("a11y-panel", `vst-theme-${theme}`);

  const loader = document.createElement("div");
  loader.id = "loader";
  loader.innerHTML = loaderHtml;

  const claims = document.createElement("div");
  claims.id = "claims";

  const claimsContent = document.createElement("div");
  claimsContent.setAttribute(
    "hx-get",
    `${workerUrl}/api/data?vbid=${vbid}&locale=${locale}`,
  );
  claimsContent.setAttribute("hx-trigger", "load");
  claimsContent.setAttribute("hx-target", "this");

  claims.appendChild(claimsContent);
  shadowContent.appendChild(loader);
  shadowContent.appendChild(claims);
  shadowRoot.appendChild(shadowContent);

  setTimeout(() => window.htmx.process(shadowContent), 100);

  shadowRoot.addEventListener("htmx:beforeRequest", () =>
    loader.classList.add("htmx-request"),
  );
  shadowRoot.addEventListener("htmx:afterRequest", () =>
    loader.classList.remove("htmx-request"),
  );
};

const generateClientScript = (
  workerUrl: string,
  vbid: string,
  locale?: string,
  theme?: string,
): string => `
  document.addEventListener('DOMContentLoaded', () => {
    const container = document.querySelector('#a11y-panel-container');
    if (container && !container.shadowRoot) {
      const shadowRoot = container.attachShadow({ mode: 'open' });

      // Load styles and HTMX script
      (${appendStylesheet.toString()})(shadowRoot, "${workerUrl}/styles/main.css");
      (${appendScript.toString()})(shadowRoot, "${HTMX_URL}", () => {
        const loaderHtml = ${JSON.stringify(buildLoader(5, formatMessage))};
        (${initializeHTMX.toString()})(shadowRoot, loaderHtml, "${workerUrl}", "${vbid}", "${locale}", "${theme}");
      });
    }
  });
`;

const initializeA11yPanel = (
  workerUrl: string,
  vbid: string,
  locale?: string,
  theme?: string,
) => {
  const containerId = "a11y-panel-container";
  const stylesUrl = `${workerUrl}/styles/main.css`;
  const loaderHtml = buildLoader(5, formatMessage);
  const { shadowRoot } = setupShadowDom(containerId, stylesUrl);

  if (shadowRoot) {
    //check if HTMX script exists
    if (!shadowRoot.querySelector(`script[src="${HTMX_URL}"]`)) {
      appendScript(shadowRoot, HTMX_URL, () => {
        //initialize panel once
        if (!shadowRoot.querySelector(".a11y-panel")) {
          initializeHTMX(
            shadowRoot,
            loaderHtml,
            workerUrl,
            vbid,
            locale,
            theme,
          );
        }
      });
    }
  }
};

//a11yPanel - module export
export const a11yPanel = ({
  vbid,
  locale,
  theme,
  dev = false,
}: A11yPanelOptions): HTMLElement => {
  const workerUrl = getWorkerUrl(dev);
  const container = document.createElement("div");
  container.id = "a11y-panel-container";

  if (typeof window !== "undefined" && document) {
    setTimeout(() => {
      if (container) {
        initializeA11yPanel(workerUrl, vbid, locale, theme);
      } else {
        console.warn(`"a11y-panel-container" not found.`);
      }
    }, 0);
  }

  return container;
};

//a11yPanelEmbed - native / embed export
export const a11yPanelEmbed = (
  { vbid, locale, theme }: A11yPanelOptions,
  workerUrl: string,
): string => {
  const containerId = "a11y-panel-container";
  const containerHtml = `<div id="${containerId}" />`;
  const clientScript = `
    <script>
      ${generateClientScript(workerUrl, vbid, locale, theme)}
    </script>
  `;

  return `
  <!DOCTYPE html>
  <html lang="${locale}">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no" />
      <title>${formatMessage("base.accessibility-claims-title")}</title>
    </head>
    <body data-vst-theme="${theme}">
      ${containerHtml}
      ${clientScript}
    </body>
  </html>`;
};
